import React, { useRef, useState } from "react";
import Modal from "react-modal";
import { useSelector } from "react-redux";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import closeIcon from "../../Assets/Svg/close-icon.svg";
import "./PdfPreviewModal.css";

function getGroupName(filterList = {}, groupName) {
  if (!groupName) return null;

  const combinedList = [
    ...(filterList.goods || []),
    ...(filterList.services || []),
    ...(filterList.project || []),
  ];

  const match = combinedList.find(
    (item) => item?.purchasinggroup_name?.trim?.() === groupName.trim()
  );

  return match?.meaning?.trim?.() || null;
}

const PDFPreviewModal = ({ isOpen, onClose, data, t }) => {
  const filterList = useSelector(
    (state) => state?.VendorJobsReducer?.filterList
  );
  const contentRef = useRef(null);
  const titleRef = useRef(null);
  const cardRefs = useRef([]);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pdfWidth - (2 * margin);
      
      let currentY = margin;
      let isFirstPage = true;

      if (titleRef.current) {
        const titleCanvas = await html2canvas(titleRef.current, {
          scale: 2.5,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        });

        const titleImgData = titleCanvas.toDataURL('image/png', 1.0);
        const titleRatio = contentWidth / (titleCanvas.width / 2.5);
        const titleHeight = (titleCanvas.height / 2.5) * titleRatio;

        pdf.addImage(
          titleImgData,
          'PNG',
          margin,
          currentY,
          contentWidth,
          titleHeight,
          undefined,
          'FAST'
        );

        currentY += titleHeight + 5;
      }

      for (let i = 0; i < cardRefs.current.length; i++) {
        const card = cardRefs.current[i];
        if (!card) continue;

        const cardCanvas = await html2canvas(card, {
          scale: 2.5,
          useCORS: true,
          logging: false,
          backgroundColor: '#fafbff'
        });

        const cardImgData = cardCanvas.toDataURL('image/png', 1.0);
        const cardRatio = contentWidth / (cardCanvas.width / 2.5);
        const cardHeight = (cardCanvas.height / 2.5) * cardRatio;

        if (currentY + cardHeight > pdfHeight - margin) {
          pdf.addPage();
          currentY = margin;
          isFirstPage = false;
        }

        pdf.addImage(
          cardImgData,
          'PNG',
          margin,
          currentY,
          contentWidth,
          cardHeight,
          undefined,
          'FAST'
        );

        currentY += cardHeight + 5;
      }

      pdf.save(`PR_Combined_Details_${data?.JobId || "export"}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="pdf-preview-modal"
      overlayClassName="pdf-preview-overlay"
      shouldCloseOnOverlayClick={false}
    >
      <div className="pdf-preview-header">
        <h2>PDF Preview</h2>
        <img
          src={closeIcon}
          alt="Close"
          onClick={onClose}
          style={{ cursor: "pointer" }}
        />
      </div>

      <div className="pdf-preview-content-wrapper">
        <div className="pdf-preview-content" ref={contentRef}>
          <div className="pdf-preview-title-section" ref={titleRef}>
            <h3>PR Combined Details</h3>
            <div className="pdf-preview-info">
              <p><strong>Job ID:</strong> {data?.JobId || "N/A"}</p>
              <p><strong>PR Title:</strong> {data?.PrTitle || "N/A"}</p>
              <p><strong>Buyer:</strong> {data?.BuyerName || "N/A"}</p>
            </div>
          </div>

          {data?.JobPrDetails?.map((item, index) => {
            const meaning = getGroupName(filterList, item.PrSapDetail?.PR_Group);
            
            return (
              <div 
                className="pdf-preview-card" 
                key={index}
                ref={(el) => (cardRefs.current[index] = el)}
              >
                <div className="pdf-preview-card-header">
                  <h4>Item {index + 1}</h4>
                </div>
                
                <div className="pdf-preview-field">
                  <span className="pdf-preview-label">PR ID:</span>
                  <span className="pdf-preview-value">
                    {item.PrSapDetail?.PR_ID || ""}
                  </span>
                </div>

                <div className="pdf-preview-field">
                  <span className="pdf-preview-label">Description:</span>
                  <span className="pdf-preview-value">
                    {item.PrSapDetail?.PR_description_Long || ""}
                  </span>
                </div>

                <div className="pdf-preview-field">
                  <span className="pdf-preview-label">Quantity:</span>
                  <span className="pdf-preview-value">
                    {item.PrSapDetail?.PR_QTY || ""}
                  </span>
                </div>

                <div className="pdf-preview-field">
                  <span className="pdf-preview-label">Unit:</span>
                  <span className="pdf-preview-value">
                    {item.PrSapDetail?.PR_Unit || ""}
                  </span>
                </div>

                <div className="pdf-preview-field">
                  <span className="pdf-preview-label">Company:</span>
                  <span className="pdf-preview-value">
                    {item.PrSapDetail?.PR_Company || ""}
                  </span>
                </div>

                <div className="pdf-preview-field">
                  <span className="pdf-preview-label">Group:</span>
                  <span className="pdf-preview-value">
                    {meaning || ""}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="pdf-preview-footer">
        <button
          type="button"
          className="pdf-preview-btn pdf-preview-cancel-btn"
          onClick={onClose}
          disabled={isDownloading}
        >
          Cancel
        </button>
        <button
          type="button"
          className="pdf-preview-btn pdf-preview-download-btn"
          onClick={handleDownloadPDF}
          disabled={isDownloading}
        >
          {isDownloading ? (
            <>
              <span className="spinner"></span>
              Generating...
            </>
          ) : (
            'Download PDF'
          )}
        </button>
      </div>
    </Modal>
  );
};

export default PDFPreviewModal;